import $ from 'jquery';
import _ from 'underscore';

import Navigation from 'app/Navigation';
import ButtonViewController from 'app/src/controllers/components/ButtonViewController';
import DetailViewController from 'app/src/controllers/components/DetailViewController';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import CCTerminal from 'app/src/data/models/CCTerminal';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Localize from 'app/src/localize/Localize';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import Terminal from 'app/src/payments/terminals/Terminal';
import TerminalFactory from 'app/src/payments/terminals/TerminalFactory';
import TerminalDetailView from 'app/src/views/settings/paymentProcessing/TerminalDetailView';
import TyroPairingHeaderView from 'app/src/views/settings/paymentProcessing/TyroPairingHeaderView';
import { TERMINAL_ATTRIBUTES } from 'shared_modules/Terminals';

export default class TerminalDetailViewController extends DetailViewController {
  /**
   * Constructor for a terminal detail view.
   *
   * @param {CCTerminal} ccTerminal   The terminal to be displayed
   * @param {GatewayConfig}   gatewayConfig   The gateway for which the terminal will be configured
   */
  constructor(ccTerminal, gatewayConfig = null) {
    if (!(ccTerminal instanceof CCTerminal)) {
      throw 'A terminal must be specified when constructing a TerminalDetailViewController';
    }

    // Blackline terminals have special text for the save button; set the options accordingly
    let detailViewOptions = {
      isNew: ccTerminal.isNew(),
    };

    if (ccTerminal.get('type') === CCTerminal.TerminalType.BLACKLINE) {
      detailViewOptions.save = {
        text: Localize.translate('Test Connection and Save'),
        inProgressText: Localize.translate('Connecting...'),
        savedText: Localize.translate('Success!'),
      };
    } else if (gatewayConfig && gatewayConfig.get('gatewayID') === GatewayConfig.GatewayType.TYRO) {
      detailViewOptions.save = {
        text: Localize.translate('Pair Terminal'),
      };
    }

    let supportedTerminalTypes = PaymentProvider.supportedTerminalsForGateway(gatewayConfig);

    super(
      new TerminalDetailView({
        model: ccTerminal,
        viewData: {
          gatewaySupportsMultipleTerminalTypes:
            supportedTerminalTypes !== null && supportedTerminalTypes.length > 1,
          configurationData: TerminalDetailViewController._getConfigurationData(
            ccTerminal,
            gatewayConfig
          ),
          supportedTerminalTypes,
          TERMINAL_ATTRIBUTES,
          readOnly: window.merchantos?.session?.isReadOnly() || false,
        },
      }),
      detailViewOptions
    );

    this._ccTerminal = ccTerminal;
    this._gatewayConfig = gatewayConfig;
  }

  /**
   * Initializes and displays the view.  Add the test connection button to default detail view.
   * */
  show() {
    super.show();

    // add the test connecton button
    this._showTestConnection();
  }

  /**
   * Save delegate method called with the save button is clicked.  Saves the terminal configuration
   * to the server.
   */
  save() {
    let deferred = $.Deferred();

    let validationErrorMessage = this._validateInput();
    if (validationErrorMessage) {
      PaymentServiceAlert.alert(validationErrorMessage);

      return deferred.reject();
    }

    this.disableDetails(true);

    // We can't simply listen for change events on the inputs because it's possible
    // for the save event to trigger before we receive a change event.
    _.each(this._detailView.getInputValues(), (value, attributeName) => {
      this._updateModelAttribute(attributeName, value);
    });

    if (
      (this._ccTerminal.get('type') === CCTerminal.TerminalType.YOXIMO ||
        this._ccTerminal.get('type') === CCTerminal.TerminalType.YOMANI) &&
      !this._ccTerminal.configuration.integrationKey
    ) {
      this._startTyroPairing().done(() => {
        deferred = this._ccTerminal
          .save()
          .done(() => {
            Navigation.navigateToClientUrl('settings/payment_processing');
          })
          .fail((jqXHR) => {
            PaymentServiceAlert.alert(jqXHR);
          })
          .always(() => {
            this.disableDetails(false);
          });
      });
    } else {
      deferred = this._ccTerminal
        .save()
        .done(() => {
          Navigation.navigateToClientUrl('settings/payment_processing');
        })
        .fail((jqXHR) => {
          PaymentServiceAlert.alert(jqXHR);
        })
        .always(() => {
          this.disableDetails(false);
        });
    }

    return deferred.promise();
  }

  /**
   * Remove delegate method called when the delete button is clicked.  Removes the terminal
   * from the server.
   */
  remove() {
    let deferred = this._ccTerminal
      .destroy()
      .done(() => {
        Navigation.navigateToClientUrl('settings/payment_processing');
      })
      .fail((jqXHR) => {
        PaymentServiceAlert.alert(jqXHR);
      });

    return deferred.promise();
  }

  /**
   * Test the connection of the configured parameters.  Currently only applies to a Cayan Genius
   * terminal.
   *
   * @returns {*}
   */
  testConnection() {
    // We can't simply listen for change events on the inputs because it's possible
    // for the save event to trigger before we receive a change event.
    _.each(this._detailView.getInputValues(), (value, attributeName) => {
      this._updateModelAttribute(attributeName, value);
    });

    let deferred = $.Deferred();
    let gatewayConfig = new GatewayConfig({
      gatewayConfigID: this._ccTerminal.get('gatewayConfigID'),
    });

    gatewayConfig
      .fetch()
      .done(() => {
        let terminal = TerminalFactory.createTerminal(gatewayConfig, this._ccTerminal);
        terminal.checkStatus().always((result) => {
          if (result === Terminal.Status.DISCONNECTED) {
            alertUser(
              Localize.translate(
                '<strong>Invalid terminal configuration.</strong><br>' +
                  'Could not reach the terminal as configured'
              )
            );

            deferred.reject();
          } else {
            deferred.resolve();
          }
        });
      })
      .fail((jqXHR) => {
        PaymentServiceAlert.alert(jqXHR);
        deferred.reject();
      });

    return deferred.promise();
  }

  /**
   * Action handler for when the Cancel button is pressed.
   *
   * @returns {*}
   */
  cancelPairing() {
    let deferred = $.Deferred().resolve();
    let terminal = TerminalFactory.createTerminal(this._gatewayConfig, this._ccTerminal);

    if (terminal) {
      terminal.cancelPairing();
    }

    this.setHeaderView();

    this.disableDetails(false);

    return deferred.promise();
  }

  /**
   * Event handler for when the terminal type (when applicable) is changed
   */
  onTerminalTypeChanged() {
    _.each(this._detailView.getInputValues(), (value, attributeName) => {
      this._updateModelAttribute(attributeName, value);
    });

    this._detailView.viewData.configurationData =
      TerminalDetailViewController._getConfigurationData(this._ccTerminal);

    this._showTestConnection();

    // re-render the view
    this._detailView.render();
  }

  /**
     @typedef {Object} ConfigurationData
     @property {string} title - The title to use in the field description
     @property {string} description - A description of the configuration data field
     @property {string} attributeName - The name to use for the field
     @property {string} attribute - The configuration attribute.  This is the field that will be updated
                                    in the model.
     @property {string} value - The value of the field (used when editing)
     @property {string} type - Identifies the type of field.  Either 'text', 'dropdown' or 'disabled'
     * */

  /**
   * Helper method to convert the terminal data into a form consumable by the view.
   *
   * @param ccTerminal {CCTerminal}   The terminal to be displayed
   * @returns {ConfigurationData[]}   The terminal specific fields to display
   * @private
   */
  static _getConfigurationData(ccTerminal) {
    let configurationData = [];

    switch (ccTerminal.get('type')) {
      case CCTerminal.TerminalType.GENIUS_MX915:
      case CCTerminal.TerminalType.GENIUS_P400:
      case CCTerminal.TerminalType.GENIUS_E285:
      case CCTerminal.TerminalType.GENIUS_M400:
        configurationData.push({
          title: Localize.translate('IP Address'),
          description: Localize.translate('Enter the IP Address of the device'),
          attributeName: Localize.translate('Address'),
          attribute: 'address',
          value: ccTerminal.configuration.address,
          type: 'text',
        });

        break;

      case CCTerminal.TerminalType.BLACKLINE:
        configurationData.push({
          title: Localize.translate('Serial Number'),
          description: Localize.translate(
            'Enter the Serial Number of the device you are trying to pair'
          ),
          attributeName: Localize.translate('Serial Number'),
          attribute: 'serialNumber',
          value: ccTerminal.configuration.serialNumber,
          type: !ccTerminal.isNew() ? 'disabled' : 'text',
        });
        break;

      case CCTerminal.TerminalType.YOXIMO:
      case CCTerminal.TerminalType.YOMANI:
        TerminalDetailViewController._addTyroFields(configurationData, ccTerminal);
        break;

      case CCTerminal.TerminalType.LIGHTSPEED:
      case CCTerminal.TerminalType.TRIPOS:
        if (ccTerminal.isNew()) {
          configurationData.push({
            title: Localize.translate('Activation Code'),
            description: Localize.translate('Enter the Activation Code displayed on the terminal'),
            attributeName: Localize.translate('Activation Code'),
            attribute: 'activationCode',
            value: ccTerminal.configuration.activationCode,
            type: 'text',
          });
        }

        configurationData.push({
          title: Localize.translate('Terminal ID'),
          description: ccTerminal.isNew()
            ? Localize.translate(
                'Enter the numeric Terminal ID if it was provided to you by the payment provider'
              ) // eslint-disable-line
            : '',
          attributeName: Localize.translate('Terminal ID'),
          attribute: 'terminalID',
          value: ccTerminal.configuration.terminalID ? ccTerminal.configuration.terminalID : null,
          type: !ccTerminal.isNew() ? 'disabled' : 'text',
          maxLength: 8,
          placeholder: '(Optional)',
        });

        // temporarily disabled the display of the terminal information until the back-end supports it

        /*                if (!ccTerminal.isNew()) {
                    configurationData.push({
                        title: Localize.translate('Terminal Model Number'),
                        attributeName: Localize.translate('Model Number'),
                        attribute: 'modelNumber',
                        value: ccTerminal.configuration.modelNumber,
                        type: 'disabled',
                    });
                    configurationData.push({
                        title: Localize.translate('Terminal Serial Number'),
                        attributeName: Localize.translate('Serial Number'),
                        attribute: 'serialNumber',
                        value: ccTerminal.configuration.serialNumber,
                        type: 'disabled',
                    });
                }
*/
        break;

      default:
        break;
    }

    return configurationData;
  }

  /**
     * Updates the configuration data with Tyro parameters
     *
     * @param {ConfigurationData[]} configurationData Tyro-specific fields are added to this data structure to
     *                                                be displayed.

     * @param {CCTerminal} ccTerminal   The terminal
     * @private
     */
  static _addTyroFields(configurationData, ccTerminal) {
    let terminalIDDescription = Localize.translate(
      'Press the Menu button on the terminal to find the Terminal ID'
    );

    configurationData.push({
      title: Localize.translate('Merchant ID'),
      description: Localize.translate(
        'Press the Menu button on the terminal to find the Merchant ID'
      ),
      attributeName: Localize.translate('Merchant ID'),
      attribute: 'merchantID',
      value: ccTerminal.configuration.merchantID,
      type: ccTerminal.configuration.integrationKey ? 'disabled' : 'text',
    });

    configurationData.push({
      title: Localize.translate('Terminal ID'),
      description: terminalIDDescription,
      attributeName: Localize.translate('Terminal ID'),
      attribute: 'terminalID',
      value: ccTerminal.configuration.terminalID,
      type: ccTerminal.configuration.integrationKey ? 'disabled' : 'text',
    });
  }

  /**
   * Updates the appropriate model attribute.
   *
   * @param attribute {string}    The name of the attribute that has changed.  It will look in
   *                              the terminals attributes or in the configuration atttribute
   *                              for an appropriate match.
   * @param value {string}        The new value of the attribute * @private
   */
  _updateModelAttribute(attribute, value) {
    if (this._ccTerminal.has(attribute)) {
      this._ccTerminal.set(attribute, value);
    } else {
      // assume the attribute is in the configuration
      let configuration = this._ccTerminal.has('configuration')
        ? this._ccTerminal.get('configuration')
        : {};

      configuration[attribute] = value;

      this._ccTerminal.set('configuration', configuration);
    }
  }

  /**
   * Performs the Tyro pairing operation
   *
   * @returns {jQuery.Promise}
   * @private
   */
  _startTyroPairing() {
    let cancelButton = new ButtonViewController({
      name: 'cancel',
      type: ButtonViewController.ButtonType.UTILITY,
      text: 'Cancel',
      inProgressText: 'Pairing...',
      actionDelegate: this.cancelPairing.bind(this),
    });

    this.setHeaderView(new TyroPairingHeaderView(), [cancelButton]);

    let terminal = TerminalFactory.createTerminal(this._gatewayConfig, this._ccTerminal);
    let deferred = $.Deferred();

    if (terminal) {
      terminal
        .pair(this._ccTerminal.configuration.merchantID, this._ccTerminal.configuration.terminalID)
        .done((integrationKey) => {
          this._updateModelAttribute('integrationKey', integrationKey);
          deferred.resolve();
          this.cancelPairing();
        })
        .fail((errorMessage) => {
          PaymentServiceAlert.alert(errorMessage);
          deferred.reject(errorMessage);
          this.cancelPairing();
        })
        .progress((progressMessage) => {
          let headerView = this.getHeaderView();
          if (headerView) {
            headerView.setStatusText(progressMessage);
          }
        });
    } else {
      this.cancelPairing();
    }

    return deferred.promise();
  }

  /**
   * Validates terminal-specific input fields
   *
   * @returns {null|String} The error message, if any
   * @private
   */
  _validateInput() {
    let ret = null;
    let validationFunction;
    let validationErrorMessage;

    // Define Terminal-specific validation
    if (
      this._ccTerminal.isNew() &&
      (this._ccTerminal.get('type') === CCTerminal.TerminalType.TRIPOS ||
        this._ccTerminal.get('type') === CCTerminal.TerminalType.LIGHTSPEED)
    ) {
      validationFunction = () => {
        let terminalIDInput = $('[name="terminalID"]').val();

        return (
          ($.isNumeric(terminalIDInput) && terminalIDInput.indexOf('.') === -1) ||
          terminalIDInput === ''
        );
      };

      validationErrorMessage = 'Terminal ID field must be numeric.';
    }

    if (validationFunction && !TerminalDetailView.validate(validationFunction)) {
      ret = validationErrorMessage;
    }

    return ret;
  }

  _showTestConnection() {
    if (
      [
        CCTerminal.TerminalType.GENIUS_MX915,
        CCTerminal.TerminalType.GENIUS_P400,
        CCTerminal.TerminalType.GENIUS_E285,
        CCTerminal.TerminalType.GENIUS_M400,
      ].includes(this._ccTerminal.get('type')) &&
      !this.getButton('testConnection')
    ) {
      this._testConnectionButton = new ButtonViewController({
        name: 'testConnection',
        type: ButtonViewController.ButtonType.SECONDARY,
        text: 'Test Connection',
        inProgressText: 'Contacting Terminal...',
        doneMessage: 'Connected!',
        actionDelegate: this.testConnection.bind(this),
      });

      this.addButton(this._testConnectionButton);
    }
  }
}
